<template>
  <div class="collect-content">
    <div class="page-title">{{ $t('message.home.myBalance') }}</div>

    <div class="balance-card">
      <div class="balance-label">{{ $t('message.home.availableBalance') }}</div>
      <div class="balance-amount">{{ displayBalance }}</div>
    </div>

    <div class="section-header">
      <div class="section-title">{{ $t('message.home.balanceHistory') }}</div>
      <div class="section-count">{{ $t('message.home.recordCount', { count: list.length }) }}</div>
    </div>
    <div v-loading="loading">
      <el-table :data="list" border style="width:100%">
        <el-table-column :label="$t('message.home.time')" min-width="160">
          <template #default="scope">
            {{ formatTime(scope.row.createdTime) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.home.type')" min-width="120">
          <template #default="scope">
            <el-tag :type="scope.row.balanceType === 0 ? 'success' : 'danger'" size="small">
              {{ scope.row.balanceType === 0 ? $t('message.home.income') : $t('message.home.expense') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.home.depositType')" min-width="120">
          <template #default="scope">
            {{ depositTypeLabel(scope.row.depositType) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.home.amount')" min-width="120">
          <template #default="scope">
            <span :class="scope.row.balanceType === 0 ? 'income' : 'expense'">
              {{ scope.row.balanceType === 0 ? '+' : '-' }}¥{{ formatMoney(scope.row.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.home.balanceAfter')" min-width="120">
          <template #default="scope">
            <span v-if="scope.row.balanceAfter != null">¥{{ formatMoney(scope.row.balanceAfter) }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.home.description')" prop="description" min-width="180" show-overflow-tooltip />
      </el-table>
      <div class="common-pagination" v-if="hasMore">
        <el-pagination
          background
          layout="prev,pager,next"
          :page-size="queryParams.pageSize"
          :current-page="queryParams.pageNumber"
          :total="paginationTotal"
          @current-change="currentChange"
        />
      </div>
      <el-empty v-if="!list.length && !loading" :description="$t('message.home.noData')" />
    </div>
  </div>
</template>

<script>
import { listBalance } from "@/api/upms/member_center";
import { load_member } from "@/api/upms/profile";

export default {
  name: "MyBalance",
  data() {
    return {
      loading: false,
      list: [],
      hasMore: false,
      paginationTotal: 0,
      currentBalance: "0.00",
      queryParams: { pageNumber: 1, pageSize: 20 },
    };
  },
  computed: {
    displayBalance() {
      const n = parseFloat(this.currentBalance);
      return Number.isNaN(n) ? "0.00" : n.toFixed(2);
    },
  },
  created() {
    this.loadBalance();
    this.loadData();
  },
  methods: {
    loadBalance() {
      load_member().then((r) => {
        const member = r.data?.data?.member;
        const b = member?.availableBalance != null ? member.availableBalance : member?.balance;
        this.currentBalance = b != null && b !== "" ? String(b) : "0.00";
      }).catch(() => {});
    },
    loadData() {
      this.loading = true;
      listBalance(this.queryParams).then((r) => {
        const data = r.data?.data;
        this.list = Array.isArray(data) ? data : (data?.content || []);
        this.hasMore = this.list.length >= this.queryParams.pageSize;
        this.paginationTotal = this.hasMore
          ? this.queryParams.pageNumber * this.queryParams.pageSize + 1
          : (this.queryParams.pageNumber - 1) * this.queryParams.pageSize + this.list.length;
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },
    currentChange(page) {
      this.queryParams.pageNumber = page;
      this.loadData();
    },
    formatMoney(v) {
      if (v == null || v === "") return "0.00";
      const n = parseFloat(v);
      return Number.isNaN(n) ? String(v) : n.toFixed(2);
    },
    formatTime(t) {
      if (t == null) return "";
      if (typeof t === "string") return t.replace("T", " ").slice(0, 19);
      if (typeof t === "number") {
        const d = new Date(t);
        if (Number.isNaN(d.getTime())) return String(t);
        const p = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
      }
      return String(t);
    },
    depositTypeLabel(v) {
      const map = {
        0: this.$t("message.home.depositTypeRecharge"),
        1: this.$t("message.home.depositTypeAdjustment"),
        2: this.$t("message.home.depositTypeOrderPayment"),
        3: this.$t("message.home.depositTypeOrderRefunds"),
        4: this.$t("message.home.depositTypeDistributionCommission"),
        5: this.$t("message.home.depositTypeDistributionWithdrawal"),
      };
      return map[v] != null ? map[v] : this.$t("message.home.depositTypeOther");
    },
  },
};
</script>

<style lang="scss" scoped>
.collect-content { padding: 24px 34px; }
.page-title { font-size: 20px; font-weight: bold; color: #333; margin-bottom: 20px; }
.balance-card {
  background: var(--theme-primary, var(--color-main, #4d6bfe));
  border-radius: 12px;
  padding: 30px;
  color: #fff;
  margin-bottom: 24px;
}
.balance-label { font-size: 14px; opacity: .85; margin-bottom: 8px; }
.balance-amount { font-size: 36px; font-weight: bold; }
.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.section-title { font-size: 16px; font-weight: bold; }
.section-count { font-size: 13px; color: #909399; }
.income { color: #67c23a; font-weight: bold; }
.expense { color: #f56c6c; font-weight: bold; }
.common-pagination { margin-top: 16px; }
</style>
